import ListItemLoading from "@/components/loading/listItemLoading/listItemLoading";
import SearchBox from "@/components/searchBox";
import {
  MerchantServiceCategoryDto,
  MerchantServiceDto,
  MerchantUserDto,
  UserAppointmentsDto,
} from "@/models/club";
import { useGetMerchantServices } from "@/services/appointment/hooks/useGetMerchantServices";
import useGetMerchantUserById from "@/services/merchantUser/hooks/useGetMerchantUserById";
import { Blockquote, Drawer, Group, Stack, Text } from "@mantine/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { SelectedMerchantServiceDto } from "./type";
import moment from "moment-jalaali";

type Props = {
  previewServices: boolean;
  closeService: () => void;
  selectedServices: SelectedMerchantServiceDto[];
  setSelectedServices: (services: SelectedMerchantServiceDto[]) => void;
  selectedMerchantServiceCategory: MerchantServiceCategoryDto | undefined;
  setSelectedMerchantServiceCategory: (
    category: MerchantServiceCategoryDto,
  ) => void;
  selectedPartner: UserAppointmentsDto | null;
  currentTime: string;
};

function SelectService({
  previewServices,
  closeService,
  selectedServices,
  setSelectedServices,
  selectedMerchantServiceCategory,
  setSelectedMerchantServiceCategory,
  selectedPartner,
  currentTime,
}: Props) {
  const [search, setSearch] = useState("");

  const { data: categories, isLoading: isLoadingCategories } =
    useGetMerchantServices(previewServices);

  const { data: partner, isLoading: isLoadingPartner } = useGetMerchantUserById(
    selectedPartner?.user?.id || 0,
  );

  const user = partner as MerchantUserDto;

  // Create filtered categories based on user's merchantServices
  const filteredCategories = useMemo(() => {
    if (!categories || !user?.merchantServices) {
      return categories || [];
    }

    // Get all service IDs from user's merchantServices
    const userServiceIds = new Set(
      user.merchantServices.map((service) => service.id),
    );

    // Filter categories and their services
    return categories
      .map((category) => ({
        ...category,
        services: category.services?.filter((service) =>
          userServiceIds.has(service.id),
        ),
      }))
      .filter((category) => category.services && category.services.length > 0);
  }, [categories, user?.merchantServices]);

  const [isSticky, setIsSticky] = useState(false);
  const stickyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current) {
        const rect = stickyRef.current.getBoundingClientRect();
        // Check if the element is stuck (top position is at or below 56px - header height)
        setIsSticky(rect.top <= 56);
      }
    };

    // Find the drawer content area - Mantine Drawer creates a .mantine-Drawer-body element
    const drawerBody = document.querySelector(
      ".mantine-Drawer-content",
    ) as HTMLElement;

    if (drawerBody) {
      drawerBody.addEventListener("scroll", handleScroll);
      return () => drawerBody.removeEventListener("scroll", handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const handleSelectService = (
    service: MerchantServiceDto,
    category: MerchantServiceCategoryDto,
  ) => {
    if (selectedServices.length === 0) {
      setSelectedServices([{ ...service, startTime: currentTime }]);
    } else {
      setSelectedServices([
        ...selectedServices,
        {
          ...service,
          startTime: moment()
            .startOf("day")
            .add(
              moment.duration(
                selectedServices[selectedServices.length - 1]?.startTime,
              ),
            )
            .add(
              selectedServices[selectedServices.length - 1]
                .durationPartInMinute,
              "minutes",
            )
            .format("HH:mm"),
        },
      ]);
    }

    setSelectedMerchantServiceCategory(category);
    closeService();
  };

  return (
    <Drawer
      opened={previewServices}
      onClose={closeService}
      position="bottom"
      radius="md"
      title={isSticky ? "انتخاب سرویس" : ""}
      size="xl"
      styles={{
        root: {
          padding: 0,
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Stack justify="start" flex={1}>
        <Text fw="bold" size="lg" px="md">
          انتخاب سرویس
        </Text>
        <Stack
          ref={stickyRef}
          styles={{
            root: {
              position: "sticky",
              top: "56px", // Header height to stick below the header
              zIndex: 15, // Higher than header but still allows proper stacking
              backgroundColor: "white",
              transition: "border-bottom 0.2s ease",
              paddingTop: isSticky ? "10px" : "0",
              paddingBottom: isSticky ? "10px" : "0",
              boxShadow: isSticky ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "none",
            },
          }}
        >
          <Stack px="md">
            <SearchBox
              placeholder="جستجو سرویس"
              title=""
              setSearch={setSearch}
            />
          </Stack>
        </Stack>

        <Stack px="md">
          {(isLoadingCategories || isLoadingPartner) && (
            <ListItemLoading count={3} />
          )}
          {filteredCategories
            ?.filter(
              (x) =>
                !selectedMerchantServiceCategory ||
                x?.id === selectedMerchantServiceCategory?.id,
            )
            ?.filter((x) => x?.services && x?.services?.length > 0)
            .filter(
              (x) =>
                x.title?.includes(search) ||
                x.services?.some((service) => service.title?.includes(search)),
            )
            .map((category, pageIndex) => (
              <React.Fragment key={pageIndex}>
                <Stack>
                  <Group justify="space-between">
                    <Text fw="500" size="lg">
                      {category?.title}
                    </Text>
                  </Group>
                  {category?.services?.map((service, serviceIndex) => (
                    <Blockquote
                      key={serviceIndex}
                      color="blue"
                      bg="white"
                      p="xs"
                      onClick={() => handleSelectService(service, category)}
                    >
                      <Group justify="space-between">
                        <Stack gap="xs">
                          <Text size="sm" fw="500">
                            {service?.title}
                          </Text>

                          <Text size="xs" c="dimmed">
                            هر {service?.durationPartInMinute} دقیقه
                          </Text>
                        </Stack>
                        <Text size="xs" fw="500">
                          {service?.startingPrice?.toLocaleString()} ریال
                        </Text>
                      </Group>
                    </Blockquote>
                  ))}
                </Stack>
              </React.Fragment>
            ))}
        </Stack>
      </Stack>
    </Drawer>
  );
}

export default SelectService;
